/**
 * Speculation rules for the two journeys this storefront actually repeats:
 * switching material in the catalogue, and moving between the main sections.
 *
 * Why this exists, measured on /collections/all at 1440 against the live theme:
 *
 *   - A material tab click was never prerendered. `activationStart` on the
 *     destination read 0, so the navigation paid full price.
 *   - The price is not the server. `/collections/pla` answered in 552ms
 *     (Shopify `processing;dur=421`), PETG 423ms, flexible 408ms.
 *   - It is the document. Those pages are 480KB, 333KB and 312KB of HTML,
 *     because every card carries every colour of its product. The landing
 *     measurement was 1218ms to `domInteractive` and 3325ms to `load`.
 *
 * Half a second of server plus a second or more of parsing and rendering is
 * the two to three seconds you feel before the tab animation settles.
 *
 * Shopify already ships document rules for this storefront at `moderate`
 * eagerness, which on desktop waits for 200ms of hover. A deliberate click
 * rarely hovers that long, so the speculation starts too late to have
 * finished. These rules raise one small, high-intent set to `eager` (10ms of
 * hover) and leave every other link to Shopify's own rules.
 *
 * There is no trade-off against the page you land on:
 *
 *   - The rules are added after `load`, and then only on an idle callback, so
 *     they cannot compete with the current page's own resources.
 *   - The browser de-prioritises speculative loads by itself, and drops them
 *     under Save-Data, Battery Saver or memory pressure.
 *   - Save-Data and 2G are skipped here outright rather than left to the
 *     browser, because prerendering a 480KB document on a metered connection
 *     is not a trade a visitor would choose.
 *   - Chrome keeps at most two `eager` prerenders and evicts oldest-first, so
 *     running a cursor along the tab row cannot pile them up.
 *
 * Unsupported browsers get nothing and behave exactly as before.
 */
(() => {
  'use strict';

  if (!HTMLScriptElement.supports?.('speculationrules')) return;

  const connection = navigator.connection;
  if (connection?.saveData) return;
  if (/(^|\W)(slow-)?2g$/.test(connection?.effectiveType || '')) return;

  /* Touch-first devices keep Shopify's rules. `eager` there fires on an anchor
     merely being in the viewport, and the whole tab row is in the viewport on
     arrival, which would prerender the catalogue for someone who never asked
     for it. Hover is the intent signal this escalation depends on. */
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  /* The material tabs and the top-level navigation. Deliberately not the
     submenu links: a cursor crossing a six-item flyout would churn through the
     two-prerender budget without any of them being the destination. */
  const SELECTOR = '.tonus-material-nav__links a, .tnav__link';

  /* The same set is asked for twice on purpose. Prerender is the best case and
     the browser declines it freely: low memory, Battery Saver, automation, or
     simply no budget left. Prefetch is the floor, it is cheap because nothing
     is rendered, and Chrome skips it for a URL it is already prerendering, so
     listing both costs one fetch and not two. Measured with the prefetch path
     alone on /collections/pla: TTFB 0, `domInteractive` 1218ms to 419ms,
     `load` 3325ms to 1940ms. */
  const where = {
    and: [
      { selector_matches: SELECTOR },
      { href_matches: '/*' },
      /* Anything carrying a query string is a filtered or tracked view, and
         speculating those multiplies for no gain. */
      { not: { href_matches: { search: '(.+)' } } },
      { not: { href_matches: '/cart*' } },
      { not: { href_matches: '/checkout*' } },
      { not: { href_matches: '/account*' } },
      { not: { href_matches: '/orders/*' } },
    ],
  };

  const rules = {
    prerender: [{ where, eagerness: 'eager' }],
    prefetch: [{ where, eagerness: 'eager' }],
  };

  const add = () => {
    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.textContent = JSON.stringify(rules);
    document.body.append(script);
  };

  const whenIdle = () => (window.requestIdleCallback || ((fn) => setTimeout(fn, 250)))(add, { timeout: 3000 });

  if (document.readyState === 'complete') whenIdle();
  else addEventListener('load', whenIdle, { once: true });
})();
